import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { titleCase } from "@/lib/text";
import { ReasonChip, NameThumb, PartTickList } from "./NamingBits";

/** A family of look-alike names. Nothing is pre-selected — a person picks the keeper. */
export function FamilyCard({ family, queue, onDone, canApply = true }) {
  const [keep, setKeep] = useState(null);
  const keeper = family.names.find((n) => n.name === keep);
  const losers = family.names.filter((n) => n.name !== keep);
  return (
    <div data-testid={`naming-family-${family.key}`} className="border border-slate-200 rounded-xl bg-white p-3">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <ReasonChip reason={family.reason} />
        <span className="text-[11px] text-slate-400">{family.names.length} names · {family.parts_total} part{family.parts_total === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-1.5">
        {family.names.map((n) => (
          <div key={n.name} className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${keep === n.name ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
            <NameThumb fileId={n.photo_file_id} alt={n.name} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-slate-800">{titleCase(n.name)}</span>
              <span className="block text-[11px] text-slate-400">{n.parts} part{n.parts === 1 ? "" : "s"}{n.in_list === false ? " · not in the Name list" : ""}</span>
            </span>
            <div className="ml-auto">
              {keep === n.name
                ? <Badge data-testid={`naming-keeper-${n.name}`} className="rounded-full bg-emerald-600 text-white border-0 text-[10px]">keeping this one</Badge>
                : <Button data-testid={`naming-keep-${n.name}`} size="sm" variant="outline" disabled={n.in_list === false || !canApply}
                    title={n.in_list === false ? "This name is not a value in the Name list — add it there first" : (!canApply ? "Applying needs the Merge list values permission" : "")}
                    onClick={() => setKeep(n.name)} className="rounded-lg h-7 text-xs">Keep this one</Button>}
            </div>
          </div>
        ))}
      </div>
      {keeper && canApply && (
        <PartTickList
          testid={`naming-tick-${family.key}`}
          lookup={{ by: "names", names: losers.map((l) => l.name) }}
          set={{ name: keeper.name }}
          queue={queue}
          archiveNames={losers.map((l) => l.id).filter(Boolean)}
          onDone={onDone}
        />
      )}
    </div>
  );
}

/** One number, more than one name / part type / category on it. */
export function NumberCard({ group, taxonomy, onDone, canApply = true }) {
  const [pick, setPick] = useState({});
  const set = {};
  if (pick.name) set.name = pick.name;
  if (pick.part_type_id) set.part_type_id = pick.part_type_id;
  if (pick.category_id) set.category_id = pick.category_id;
  const chosen = Object.keys(set).length > 0;
  const typeCat = taxonomy.part_types.find((t) => t.id === pick.part_type_id);
  return (
    <div data-testid={`naming-number-${group.number}`} className="border border-slate-200 rounded-xl bg-white p-3">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-mono text-sm font-bold text-slate-800">{group.number}</span>
        <Badge className="rounded-full bg-red-50 text-[#B01E28] border border-red-200 text-[10px]">
          disagrees on {group.disagrees_on.join(" & ")}
        </Badge>
        <span className="text-[11px] text-slate-400">{group.parts_total} parts{group.unnamed ? ` · ${group.unnamed} with no name` : ""}</span>
      </div>
      {group.names.length > 1 && (
        <div className="space-y-1.5 mb-2">
          {group.names.map((n) => (
            <div key={n.name} className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${pick.name === n.name ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
              <NameThumb fileId={n.photo_file_id} alt={n.name} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{titleCase(n.name)}</span>
                <span className="block text-[11px] text-slate-400">{n.parts} part{n.parts === 1 ? "" : "s"} in the yard{n.in_list === false ? " · not in the Name list" : ""}</span>
              </span>
              <div className="ml-auto">
                {pick.name === n.name
                  ? <Badge className="rounded-full bg-emerald-600 text-white border-0 text-[10px]">this name</Badge>
                  : <Button data-testid={`naming-num-name-${n.name}`} size="sm" variant="outline" disabled={n.in_list === false || !canApply}
                      onClick={() => setPick((p) => ({ ...p, name: n.name }))} className="rounded-lg h-7 text-xs">Use this name</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        {group.disagrees_on.includes("part type") && (
          <Select value={pick.part_type_id || ""} disabled={!canApply} onValueChange={(v) => setPick((p) => ({ ...p, part_type_id: v }))}>
            <SelectTrigger data-testid={`naming-num-type-${group.number}`} className="h-9 w-64 rounded-lg text-sm"><SelectValue placeholder="Pick the part type…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {group.types.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{titleCase(t.name)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {group.disagrees_on.includes("category") && (
          <Select value={pick.category_id || ""} disabled={!canApply} onValueChange={(v) => setPick((p) => ({ ...p, category_id: v }))}>
            <SelectTrigger data-testid={`naming-num-cat-${group.number}`} className="h-9 w-56 rounded-lg text-sm"><SelectValue placeholder="Pick the category…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {group.categories.map((c) => <SelectItem key={c.id} value={c.id} className="text-sm">{titleCase(c.name)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {typeCat && !pick.category_id && (
        <div className="text-[11px] text-slate-500 mt-2">
          {titleCase(typeCat.name)} lives under <b>{titleCase(taxonomy.categories.find((c) => c.id === typeCat.category_id)?.name || "—")}</b>.
          <Button data-testid={`naming-num-cat-suggest-${group.number}`} size="sm" variant="link" className="h-6 px-1 text-[11px]"
            onClick={() => setPick((p) => ({ ...p, category_id: typeCat.category_id }))}>Use that category too</Button>
        </div>
      )}
      {chosen && canApply && (
        <PartTickList testid={`naming-tick-${group.number}`} lookup={{ by: "number", number: group.number }}
          set={set} queue="numbers" onDone={onDone} />
      )}
    </div>
  );
}

/** One name, N parts with no part type or no category. One decision covers them all. */
export function MissingCard({ group, taxonomy, onDone, canApply = true }) {
  const [pick, setPick] = useState({});
  const set = {};
  if (pick.part_type_id) set.part_type_id = pick.part_type_id;
  else if (pick.category_id) set.category_id = pick.category_id;
  const typeCat = taxonomy.part_types.find((t) => t.id === pick.part_type_id);
  return (
    <div data-testid={`naming-missing-${group.key}`} className="border border-slate-200 rounded-xl bg-white p-3">
      <div className="flex items-center gap-2.5 mb-2">
        <NameThumb fileId={group.photo_file_id} alt={group.name} />
        <span className="min-w-0">
          <span className="block text-sm font-bold text-slate-800">{titleCase(group.name)}</span>
          <span className="block text-[11px] text-slate-400">
            {group.parts_total} part{group.parts_total === 1 ? "" : "s"}
            {group.no_type ? ` · ${group.no_type} with no part type` : ""}{group.no_cat ? ` · ${group.no_cat} with no category` : ""}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Select value={pick.part_type_id || ""} disabled={!canApply} onValueChange={(v) => setPick({ part_type_id: v })}>
          <SelectTrigger data-testid={`naming-missing-type-${group.key}`} className="h-9 w-64 rounded-lg text-sm"><SelectValue placeholder="Pick the part type…" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {taxonomy.part_types.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{titleCase(t.name)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pick.category_id || ""} disabled={!canApply} onValueChange={(v) => setPick({ category_id: v })}>
          <SelectTrigger data-testid={`naming-missing-cat-${group.key}`} className="h-9 w-56 rounded-lg text-sm"><SelectValue placeholder="…or just the category" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {taxonomy.categories.map((c) => <SelectItem key={c.id} value={c.id} className="text-sm">{titleCase(c.name)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {typeCat && (
        <div className="text-[11px] text-slate-500 mt-2">
          Applies the part type only. {titleCase(typeCat.name)} lives under <b>{titleCase(taxonomy.categories.find((c) => c.id === typeCat.category_id)?.name || "—")}</b> — set that in a second pass if you want it.
        </div>
      )}
      {Object.keys(set).length > 0 && canApply && (
        <PartTickList testid={`naming-tick-${group.key}`} lookup={{ by: "names", names: [group.name] }}
          set={set} queue="missing" onDone={onDone} />
      )}
    </div>
  );
}

/** Group Apply — one identified part, its lot-mates that match. */
export function GroupApplyCard({ group, onDone, canApply = true }) {
  const [field, setField] = useState(null);
  const set = field === "name" ? { name: group.source.name }
    : field === "part_type_id" ? { part_type_id: group.source.part_type_id }
      : field === "category_id" ? { category_id: group.source.category_id } : null;
  return (
    <div data-testid={`naming-group-${group.key}`} className="border border-slate-200 rounded-xl bg-white p-3">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-[10px] font-mono">{group.lot.batch_number || "lot"}</Badge>
        <span className="text-sm font-medium text-slate-800">{titleCase(group.source.name || "")}</span>
        <span className="text-[11px] text-slate-400 font-mono">{group.source.short_code}</span>
        <span className="text-[11px] text-slate-400">
          {group.source.part_type_name || "no part type"}{group.source.category_name ? ` · ${group.source.category_name}` : ""}
        </span>
      </div>
      <div className="text-[11px] text-slate-500 mb-2">
        {group.parts_total} part{group.parts_total === 1 ? "" : "s"} in this lot match it ({[...new Set(group.candidates.map((c) => c.basis))].join(" / ")}) and are missing {[...new Set(group.candidates.flatMap((c) => c.needs))].join(", ")}.
      </div>
      <div className="flex flex-wrap gap-2">
        {group.source.name && <Button data-testid={`naming-group-name-${group.key}`} size="sm" variant={field === "name" ? "default" : "outline"} disabled={!canApply} onClick={() => setField("name")} className="rounded-lg h-8 text-xs">Copy the name</Button>}
        {group.source.part_type_id && <Button data-testid={`naming-group-type-${group.key}`} size="sm" variant={field === "part_type_id" ? "default" : "outline"} disabled={!canApply} onClick={() => setField("part_type_id")} className="rounded-lg h-8 text-xs">Copy the part type</Button>}
        {group.source.category_id && <Button data-testid={`naming-group-cat-${group.key}`} size="sm" variant={field === "category_id" ? "default" : "outline"} disabled={!canApply} onClick={() => setField("category_id")} className="rounded-lg h-8 text-xs">Copy the category</Button>}
      </div>
      {set && canApply && (
        <PartTickList testid={`naming-tick-${group.key}`} lookup={{ by: "ids", ids: group.candidates.map((c) => c.id) }}
          set={set} queue="group" onDone={onDone} />
      )}
    </div>
  );
}
